import type { GitNeedsAction, GitPrepared, GitSaved, PublishedApp } from './publish-stream';
import type { PublishRowState } from './pro-builder-publish-store';

export type GitActionResult =
  | ({ status: 'prepared' } & GitPrepared)
  | ({ status: 'saved' } & GitSaved)
  | { status: 'requested'; message: string }
  | { status: 'published'; app: PublishedApp; created: boolean; warnings: string[]; publishId?: number }
  | ({ status: 'needs-action' } & GitNeedsAction)
  | { status: 'ready'; message: string; paths?: string[]; restartRequired?: boolean };

export interface GitResultDeps {
  isBound: () => boolean;
  setRow: (row: PublishRowState | null) => void;
  published: (result: { app: PublishedApp; created: boolean; warnings: string[]; publishId?: number }) => void;
  invalidate: () => void;
  restart: () => void;
  prepared?: GitPrepared;
}

export function gitProjectPath(name: string): string {
  return `/api/pro-builder/projects/${encodeURIComponent(name)}/git`;
}
export function applyGitResult(result: GitActionResult, deps: GitResultDeps): void {
  deps.invalidate();
  if (!deps.isBound()) return;
  if (result.status === 'prepared') {
    const { status: _status, ...prepared } = result;
    deps.setRow({ phase: 'prepared', prepared });
  } else if (result.status === 'published') {
    const { status: _status, ...published } = result;
    const undo = typeof result.publishId === 'number' ? { slug: result.app.slug, publishId: result.publishId } : undefined;
    deps.setRow(result.warnings.length || undo ? { phase: 'done', warnings: result.warnings, ...(undo ? { undo } : {}) } : null);
    deps.published(published);
  } else if (result.status === 'needs-action' && deps.prepared && result.confirmation) {
    deps.setRow({ phase: 'prepared', prepared: deps.prepared, action: result });
  } else {
    const { status, ...body } = result;
    deps.setRow({ phase: status, ...body } as PublishRowState);
  }
  if ('restartRequired' in result && result.restartRequired) deps.restart();
}
